import {
  MapPin,
  Calendar,
  History,
  LogOut,
  Settings,
  Wallet,
  Store,
  Info,
  CircleHelp,
  PackageSearch,
  RotateCcw,
} from "lucide-react";
import stylReLogo from "@/assets/stylere-logo.png";
import { ROUTES, navigateTo } from "@/lib/navigation";
import { getOrderFlowRoute } from "@/lib/orderFlowRoute";
import { useAuth } from "@/hooks/use-auth";
import { useOrderPickupStore } from "@/stores/orderPickupStore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import LayoutContainer from "./LayoutContainer";
import { isReturnPath } from "@/lib/returnFlow";

export default function Header() {
  const location = useLocation();
  const { user, isLoading, logout, isAuthenticated, checkSensitiveAuthStatus } =
    useAuth();
  const inReturns = isReturnPath(location.pathname);
  const { step, orderPickup, store } = useOrderPickupStore();
  const hasIncompletePickup =
    !inReturns &&
    Boolean(store?._id || orderPickup?.storeId || orderPickup?._id || orderPickup?.orderId) &&
    !["complete", "completed", "delivered", "cancelled", "canceled", "refunded", "order placed"].includes(
      String(orderPickup?.status || "").toLowerCase(),
    );
  const isMemberExperience =
    location.pathname.startsWith("/member") ||
    location.pathname.startsWith("/schedule-pickup") ||
    location.pathname === ROUTES.PICKUP_HISTORY ||
    location.pathname === ROUTES.WALLET ||
    location.pathname === ROUTES.SETTINGS ||
    location.pathname === "/subscribe" ||
    location.pathname.startsWith("/membership");

  // Get initials for avatar fallback
  const getInitials = () => {
    if (!user) return "U";

    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }

    if (user.username) {
      return user.username.substring(0, 2).toUpperCase();
    }

    return user.email.substring(0, 2).toUpperCase();
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigateTo(ROUTES.HOME);
  };

  // Handle login navigation
  const handleLogin = () => {
    navigateTo("/auth?tab=login");
  };

  // Handle sensitive navigation - server-side check, client-side routing
  const handleSensitiveNavigation = async (route: string) => {
    const targetRoute =
      route === ROUTES.SCHEDULE_PICKUP
        ? getOrderFlowRoute({ step, orderPickup, store })
        : route;

    if (!isAuthenticated) {
      // User not logged in at all
      sessionStorage.setItem("redirectAfterLogin", targetRoute);
      navigateTo("/auth");
      return;
    }

    try {
      // Check with server if user has recent auth
      const hasRecentAuth = await checkSensitiveAuthStatus();

      if (hasRecentAuth) {
        navigateTo(targetRoute);
      } else {
        sessionStorage.setItem("redirectAfterSensitiveAuth", targetRoute);
        navigateTo("/re-authenticate");
      }
    } catch {
      // On error, assume we need re-auth
      sessionStorage.setItem("redirectAfterSensitiveAuth", targetRoute);
      navigateTo("/re-authenticate");
    }
  };

  const memberNav = [
    { to: "/member/stores", label: "Stores", icon: Store },
    { to: ROUTES.MEMBER_RETURNS_STORE_DETAILS, label: "Returns", icon: RotateCcw },
    { to: "/member/how-it-works", label: "How It Works", icon: Info },
    { to: "/member/benefits", label: "Benefits", icon: Settings },
    { to: "/member/help", label: "Help", icon: CircleHelp },
  ];

  const publicNav = [
    { to: "/how-it-works", label: "How It Works" },
    { to: ROUTES.STORE_SELECTION, label: "Stores" },
    { to: ROUTES.RETURNS_STORE_DETAILS, label: "Returns" },
    { to: "/help-center", label: "Help", className: "hidden xl:inline-flex" },
  ];

  return (
    <header
      data-site-header
      className="site-global-header fixed left-0 top-0 z-50 flex h-[82px] w-screen items-center bg-white text-base"
    >
      <LayoutContainer className="md:px-6 xl:px-8 2xl:px-[50px]">
        {/* Logo */}
        <div className="flex min-w-0 items-center justify-between gap-4">
          <div className="shrink-0">
            <a
              href={ROUTES.HOME}
              className="flex items-center"
              onClick={(e) => {
                e.preventDefault();
                navigateTo(ROUTES.HOME);
              }}
            >
              <img width="172" src={stylReLogo} alt="style.re logo" className="h-auto w-[148px] xl:w-[172px]" />
            </a>
          </div>

          <div className="ml-2 hidden min-w-0 flex-1 items-center justify-center gap-4 lg:flex xl:gap-5 2xl:gap-7" aria-label={isMemberExperience ? "Member navigation" : "Public navigation"}>
            {(isMemberExperience && user ? memberNav : publicNav).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`${"className" in item ? item.className : ""} whitespace-nowrap text-xs font-black transition-colors xl:text-sm ${
                  location.pathname === item.to ? "text-[#ff5a1f]" : "text-[#071229] hover:text-[#ff5a1f]"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="ml-auto flex shrink-0 items-center">
            {/* User authentication */}
            {isLoading ? (
              <div className="w-8 h-8 ml-4 rounded-full bg-gray-900/10 animate-pulse"></div>
            ) : /* if user exists (logged in) */
            user ? (
              <>
                {hasIncompletePickup && (
                  <Link to={getOrderFlowRoute({ step, orderPickup, store })}>
                    <Button
                      size="lg"
                      className="self-center md:flex hidden px-4 py-2 mr-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors  items-center"
                    >
                      <Calendar className="h-4 w-4" />
                      Continue Scheduling
                    </Button>
                  </Link>
                )}
                <Link to={inReturns ? ROUTES.MEMBER_RETURNS_STORE_DETAILS : isMemberExperience ? "/schedule-pickup/store-details" : ROUTES.STORE_DETAILS}>
                  <Button
                    size="lg"
                    className="self-center lg:flex hidden px-4 py-2 mr-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors items-center"
                  >
                    {inReturns ? <RotateCcw className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
                    {inReturns ? "Schedule Return" : "Schedule a Pickup"}
                  </Button>
                </Link>
                <Link to={isMemberExperience ? "/settings" : ROUTES.PICKUP_HISTORY}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="self-center md:flex hidden px-4 py-2 items-center"
                  >
                    {isMemberExperience && !inReturns ? <Settings className="h-4 w-4" /> : <PackageSearch className="h-4 w-4" />}
                    {inReturns ? "Track Return" : isMemberExperience ? "Manage Membership" : "Track Order"}
                  </Button>
                </Link>

                {/* <a
							href={ROUTES.PICKUP_HISTORY}
							className="px-4 py-2 ml-4 border border-transparent text-sm font-medium rounded-full text-black bg-primary/25 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 flex items-center justify-center"
							onClick={(e) => {
								e.preventDefault()
								navigateTo(ROUTES.PICKUP_HISTORY)
							}}
						>
							Pickup History
						</a> */}

                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="cursor-pointer relative h-8 w-8 rounded-full ml-4"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>{getInitials()}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        {/* If user auth is available, use this */}
                        {/* <p className="text-sm font-medium leading-none">
										{user.firstName
											? `${user.firstName} ${user.lastName || ""}`
											: user.username}
									</p>
									<p className="text-xs leading-none text-muted-foreground">
										{user.email}
									</p> */}

                        {/* <p className="text-sm font-medium leading-none">
											{user && user.firstName
												? `${user.firstName} ${user.lastName || ""}`
												: user?.username}
										</p> */}
                        <div className="flex items-center space-x-1">
                          <MapPin className="h-5 w-5 mr-2 text-neutral-600" />
                          <span className="block text-sm text-neutral-700 font-semibold truncate max-w-[180px]">
                            {user?.streetAddress || "Set your address"}
                          </span>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    {isMemberExperience && memberNav.map((item) => {
                      const Icon = item.icon;
                      return (
                        <DropdownMenuItem
                          key={item.to}
                          className="flex xl:hidden"
                          onClick={() => navigateTo(item.to)}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuItem
                      className="flex xl:hidden"
                      onClick={() => navigateTo("/membership/dashboard")}
                    >
                      <Settings className="h-4 w-4" />
                      <span>Membership Dashboard</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                      onClick={() => handleSensitiveNavigation(ROUTES.WALLET)}
                    >
                      <Wallet className="mr-2 h-4 w-4" />
                      <span>Wallet</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigateTo(ROUTES.SETTINGS)}
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Account Settings</span>
                    </DropdownMenuItem>
                    {hasIncompletePickup && (
                      <DropdownMenuItem
                        className="flex md:hidden"
                        onClick={() =>
                          handleSensitiveNavigation(ROUTES.SCHEDULE_PICKUP)
                        }
                      >
                        <Calendar className="h-4 w-4 " />
                        <span>Continue Scheduling</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      className="flex lg:hidden"
                      onClick={() =>
                        handleSensitiveNavigation(inReturns ? ROUTES.MEMBER_RETURNS_STORE_DETAILS : ROUTES.STORE_DETAILS)
                      }
                    >
                      {inReturns ? <RotateCcw className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
                      <span>{inReturns ? "Schedule Return" : "Schedule Pickup"}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      className="flex md:hidden"
                      onClick={() =>
                        handleSensitiveNavigation(ROUTES.PICKUP_HISTORY)
                      }
                    >
                      <History className="h-4 w-4" />
                      <span>Pickup History</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <p className="pl-2 text-s text-muted-foreground">
                      {user?.email || ""}
                    </p>
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4 text-gray-500" />
                      <span className="text-gray-500">Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-1.5 xl:gap-2">
                {hasIncompletePickup && (
                  <Button
                    size="sm"
                    className="hidden h-9 whitespace-nowrap px-3 text-xs font-bold text-white md:inline-flex xl:text-sm"
                    onClick={() => navigateTo(getOrderFlowRoute({ step, orderPickup, store }))}
                  >
                    Continue Scheduling
                  </Button>
                )}
                <Button
                  size="sm"
                  className="hidden h-9 whitespace-nowrap px-3 text-xs font-bold text-white md:inline-flex xl:px-4 xl:text-sm"
                  onClick={() => navigateTo(inReturns ? ROUTES.RETURNS_STORE_DETAILS : ROUTES.STORE_DETAILS)}
                >
                  {inReturns ? <RotateCcw className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
                  {inReturns ? "Schedule Return" : "Schedule a Pickup"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden h-9 whitespace-nowrap px-3 text-xs font-semibold border-neutral-300 text-neutral-700 hover:border-neutral-500 hover:text-neutral-900 md:inline-flex xl:text-sm"
                  onClick={() => navigateTo(ROUTES.PICKUP_HISTORY)}
                >
                  <PackageSearch className="h-4 w-4" />
                  {inReturns ? "Track Return" : "Track Order"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 whitespace-nowrap px-3 text-xs font-semibold border-neutral-300 text-neutral-700 hover:border-neutral-500 hover:text-neutral-900 flex xl:text-sm"
                  onClick={handleLogin}
                >
                  Sign in
                </Button>
                <Button
                  size="sm"
                  className="h-9 whitespace-nowrap px-3 text-xs font-bold text-black shadow-sm xl:px-4 xl:text-sm"
                  style={{
                    background: "linear-gradient(135deg, #E8C170 0%, #c9972a 100%)",
                    border: "none",
                  }}
                  onClick={() => navigateTo('/auth?tab=register')}
                >
                  Register
                </Button>
              </div>
            )}

            {/* <span>Menu</span>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
					className="lucide lucide-square-menu-icon lucide-square-menu"
				>
					<rect
						width="18"
						height="18"
						x="3"
						y="3"
						rx="2"
					/>
					<path d="M7 8h10" />
					<path d="M7 12h10" />
					<path d="M7 16h10" />
				</svg> */}
          </div>
        </div>
      </LayoutContainer>
    </header>
  );
}
